import { useEffect, useRef } from 'react';

export function ParticlesBackground() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create canvas for particles
    const canvas = document.createElement('canvas');
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    canvas.style.willChange = 'transform';
    container.appendChild(canvas);

    const context = canvas.getContext('2d', { alpha: true, antialias: false }) as CanvasRenderingContext2D | null;
    if (!context) return;
    const ctx: CanvasRenderingContext2D = context;

    // Bubble configuration
    interface Bubble {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      opacity: number;
      floatSpeed: number;
      color: 'blue' | 'pink';
      exploding?: boolean;
      explosionTime?: number;
      explosionParticles?: Array<{ x: number; y: number; vx: number; vy: number; life: number; color: 'blue' | 'pink' }>;
      merging?: boolean;
      targetRadius?: number | undefined;
      mergeProgress?: number;
      lastCollisionCheck?: number;
    }

    const bubbles: Bubble[] = [];
    const TARGET_BUBBLE_COUNT = 12;
    let collisionCheckCounter = 0;
    const COLLISION_CHECK_INTERVAL = 2; // Check collisions every 2 frames

    // Simple spatial grid for faster collision detection
    class SpatialGrid {
      cellSize: number;
      grid: Map<string, Bubble[]>;

      constructor(cellSize: number) {
        this.cellSize = cellSize;
        this.grid = new Map();
      }

      clear() {
        this.grid.clear();
      }

      insert(bubble: Bubble) {
        const cell = `${Math.floor(bubble.x / this.cellSize)},${Math.floor(bubble.y / this.cellSize)}`;
        if (!this.grid.has(cell)) this.grid.set(cell, []);
        this.grid.get(cell)!.push(bubble);
      }

      getNearby(bubble: Bubble): Bubble[] {
        const cx = Math.floor(bubble.x / this.cellSize);
        const cy = Math.floor(bubble.y / this.cellSize);
        const nearby: Bubble[] = [];

        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const cell = `${cx + dx},${cy + dy}`;
            if (this.grid.has(cell)) {
              nearby.push(...this.grid.get(cell)!);
            }
          }
        }
        return nearby;
      }
    }

    const spatialGrid = new SpatialGrid(100);

    // Function to create a new bubble
    function createBubble(): Bubble {
      const isBlue = Math.random() > 0.5;
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2 - 0.1,
        radius: Math.random() * 18 + 10,
        opacity: Math.random() * 0.5 + 0.5,
        floatSpeed: Math.random() * 0.01 + 0.002,
        color: isBlue ? 'blue' : 'pink' as const,
      };
    }

    // Initialize bubbles
    for (let i = 0; i < TARGET_BUBBLE_COUNT; i++) {
      bubbles.push(createBubble());
    }

    let time = 0;
    let lastFrameTime = 0;
    const targetFPS = 60;
    const frameDuration = 1000 / targetFPS;

    // Draw a realistic water bubble
    function drawBubble(bubble: Bubble) {
      const x = bubble.x;
      const y = bubble.y;
      const r = bubble.radius;

      // Set colors based on bubble color
      let fillColor0, fillColor1, fillColor2, outlineColor;

      if (bubble.color === 'blue') {
        fillColor0 = `rgba(220, 240, 255, ${bubble.opacity})`;
        fillColor1 = `rgba(150, 200, 255, ${bubble.opacity * 0.8})`;
        fillColor2 = `rgba(80, 150, 220, ${bubble.opacity * 0.6})`;
        outlineColor = `rgba(50, 130, 200, ${bubble.opacity * 0.9})`;
      } else {
        fillColor0 = `rgba(255, 220, 240, ${bubble.opacity})`;
        fillColor1 = `rgba(255, 150, 200, ${bubble.opacity * 0.8})`;
        fillColor2 = `rgba(220, 80, 150, ${bubble.opacity * 0.6})`;
        outlineColor = `rgba(200, 50, 130, ${bubble.opacity * 0.9})`;
      }

      // Main bubble body - gradient for 3D effect
      const gradient = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, 0, x, y, r);
      gradient.addColorStop(0, fillColor0);
      gradient.addColorStop(0.5, fillColor1);
      gradient.addColorStop(1, fillColor2);

      ctx.globalAlpha = bubble.opacity;
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();

      // Outer bubble outline
      ctx.strokeStyle = outlineColor;
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // Shine/highlight on top
      const shineGradient = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, 0, x - r * 0.35, y - r * 0.35, r * 0.4);
      shineGradient.addColorStop(0, `rgba(255, 255, 255, ${bubble.opacity * 0.9})`);
      shineGradient.addColorStop(0.6, `rgba(255, 255, 255, ${bubble.opacity * 0.3})`);
      shineGradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
      ctx.fillStyle = shineGradient;
      ctx.beginPath();
      ctx.arc(x - r * 0.35, y - r * 0.35, r * 0.4, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw explosion particles
    function drawExplosion(bubble: Bubble) {
      if (!bubble.explosionParticles) return;

      bubble.explosionParticles.forEach((particle) => {
        ctx.globalAlpha = particle.life;
        ctx.fillStyle = particle.color === 'blue' ? `rgba(100, 180, 255, 0.6)` : `rgba(255, 100, 180, 0.6)`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, 2.5, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // Animation loop with throttling
    const animate = (currentTime: number) => {
      if (lastFrameTime && currentTime - lastFrameTime < frameDuration) {
        requestAnimationFrame(animate);
        return;
      }
      lastFrameTime = currentTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.01;

      // Maintain target bubble count - only add if below target
      while (bubbles.length < TARGET_BUBBLE_COUNT) {
        bubbles.push(createBubble());
      }

      // Update bubbles
      for (let i = 0; i < bubbles.length; i++) {
        const bubble = bubbles[i];
        if (!bubble) continue;

        if (bubble.exploding) {
          if (!bubble.explosionTime) bubble.explosionTime = 0;
          bubble.explosionTime += 0.1;

          if (bubble.explosionParticles) {
            bubble.explosionParticles.forEach((particle) => {
              particle.x += particle.vx;
              particle.y += particle.vy;
              particle.vy += 0.08;
              particle.life -= 0.08;
            });

            drawExplosion(bubble);

            if (bubble.explosionTime > 0.3) {
              bubbles.splice(i, 1);
              i--;
              continue;
            }
          }
        } else if (bubble.merging && bubble.targetRadius) {
          if (!bubble.mergeProgress) bubble.mergeProgress = 0;
          bubble.mergeProgress += 0.05;

          const progress = Math.min(bubble.mergeProgress, 1);
          bubble.radius = bubble.radius * (1 - progress) + bubble.targetRadius * progress;

          if (progress >= 1) {
            bubble.merging = false;
            bubble.mergeProgress = 0;
            bubble.targetRadius = undefined;
          }

          drawBubble(bubble);
        } else {
          bubble.vx *= 0.99;
          bubble.vy *= 0.99;

          bubble.x += bubble.vx + Math.sin(time * bubble.floatSpeed + i) * 0.12;
          bubble.y += bubble.vy + Math.cos(time * bubble.floatSpeed + i) * 0.08 - 0.04;

          if (bubble.x < -bubble.radius) bubble.x = canvas.width + bubble.radius;
          if (bubble.x > canvas.width + bubble.radius) bubble.x = -bubble.radius;
          if (bubble.y < -bubble.radius) bubble.y = canvas.height + bubble.radius;
          if (bubble.y > canvas.height + bubble.radius) bubble.y = -bubble.radius;

          drawBubble(bubble);
        }
      }

      // Collision detection - only every N frames
      if (collisionCheckCounter++ % COLLISION_CHECK_INTERVAL === 0) {
        spatialGrid.clear();
        bubbles.forEach((b) => spatialGrid.insert(b));

        for (let i = 0; i < bubbles.length; i++) {
          const bubble = bubbles[i];
          if (!bubble || bubble.exploding || bubble.merging) continue;

          const nearby = spatialGrid.getNearby(bubble);
          for (const other of nearby) {
            if (other === bubble || other.exploding || other.merging) continue;

            const dx = other.x - bubble.x;
            const dy = other.y - bubble.y;
            const distSq = dx * dx + dy * dy;
            const minDist = bubble.radius + other.radius;

            if (distSq < minDist * minDist) {
              if (bubble.color === other.color) {
                const larger = bubble.radius >= other.radius ? bubble : other;
                const smaller = bubble.radius >= other.radius ? other : bubble;
                const smallerIdx = bubble.radius >= other.radius ? bubbles.indexOf(other) : i;

                const newRadius = Math.sqrt(larger.radius ** 2 + smaller.radius ** 2);
                larger.merging = true;
                larger.targetRadius = newRadius;
                larger.mergeProgress = 0;

                bubbles.splice(smallerIdx, 1);
              } else {
                const smaller = bubble.radius < other.radius ? bubble : other;

                smaller.exploding = true;
                smaller.explosionParticles = [];
                for (let k = 0; k < 8; k++) {
                  const angle = (Math.PI * 2 * k) / 8;
                  smaller.explosionParticles.push({
                    x: smaller.x + Math.cos(angle) * smaller.radius * 1.5,
                    y: smaller.y + Math.sin(angle) * smaller.radius * 1.5,
                    vx: Math.cos(angle) * 3.5,
                    vy: Math.sin(angle) * 3.5,
                    life: 1,
                    color: smaller.color,
                  });
                }
              }
            }
          }
        }
      }

      ctx.globalAlpha = 1;
      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);

    const handleClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      for (const bubble of bubbles) {
        if (!bubble.exploding && !bubble.merging) {
          const dx = bubble.x - clickX;
          const dy = bubble.y - clickY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < bubble.radius) {
            bubble.exploding = true;
            bubble.explosionParticles = [];
            for (let i = 0; i < 8; i++) {
              const angle = (Math.PI * 2 * i) / 8;
              bubble.explosionParticles.push({
                x: bubble.x + Math.cos(angle) * bubble.radius * 1.5,
                y: bubble.y + Math.sin(angle) * bubble.radius * 1.5,
                vx: Math.cos(angle) * 3.5,
                vy: Math.sin(angle) * 3.5,
                life: 1,
                color: bubble.color,
              });
            }
            break;
          }
        }
      }
    };

    const handleResize = () => {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    };

    window.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleResize);
      canvas.remove();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden cursor-pointer"
      style={{ background: 'linear-gradient(135deg, rgb(255, 255, 255) 0%, rgb(248, 250, 252) 100%)' }}
    />
  );
}
